import React from 'react';
import BaseModal from '../BaseModal/BaseModal';
import PaymentForm from '../PaymentForm/PaymentForm';
import PendingDialog from '../PendingDialog/PendingDialog';
import SuccessDialog from '../SuccessDialog/SuccessDialog';
import FailedDialog from '../FailedDialog/FailedDialog';
import ExpiredDialog from '../ExpiredDialog/ExpiredDialog';
import LoadingDialog from '../LoadingDialog/LoadingDialog';
import UnavailableDialog from '../UnavailableDialog/UnavailableDialog';
import { usePayment } from '../../hooks/usePayment';
import { formatTime } from '../../utils/urlUtils';

interface PaymentModalProps {
  paymentId: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ paymentId }) => {
  const {
    paymentData,
    loading,
    error,
    confirming,
    timeRemaining,
    confirmPayment,
    reFetchPayment,
    requiresConfirmRef
  } = usePayment(paymentId);
  const [refreshing, setRefreshing] = React.useState(false);

  const handleFormSubmit = async (ref?: string) => {
    await confirmPayment(ref);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await reFetchPayment();
    setRefreshing(false);
  };

  const backToSite = () => {
    const redirectUrl = paymentData?.details?.redirect_url;

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  if (loading && !paymentData) {
    return (
      <div className="grid-item">
        <LoadingDialog />
      </div>
    );
  }

  if (error && !paymentData) {
    return (
      <div className="grid-item">
        <UnavailableDialog />
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="grid-item">
        <UnavailableDialog />
      </div>
    );
  }

  const isExpired = paymentData.status === 'expired';
  const isFailed = paymentData.status === 'failed' || paymentData.status === 'dispute';
  const isSuccess = paymentData.status === 'success';
  const isConfirmed = paymentData.status === 'confirmed';

  if (isSuccess) {
    const redirectUrl = paymentData?.details?.redirect_url;

    return (
      <div className="grid-item">
        <SuccessDialog
          onBackToMerchant={redirectUrl ? backToSite : undefined}
        />
      </div>
    );
  }

  if (isFailed) {
    const redirectUrl = paymentData?.details?.redirect_url;

    return (
      <div className="grid-item">
        <FailedDialog
          onBackToMerchant={redirectUrl ? backToSite : undefined}
        />
      </div>
    );
  }

  if (isExpired) {
    const redirectUrl = paymentData?.details?.redirect_url;

    return (
      <div className="grid-item">
        <ExpiredDialog
          onBackToMerchant={redirectUrl ? backToSite : undefined}
        />
      </div>
    );
  }

  if (isConfirmed) {
    const redirectUrl = paymentData?.details?.redirect_url;

    return (
      <div className="grid-item">
        <PendingDialog
          timeRemaining={timeRemaining}
          onRefresh={handleRefresh}
          onBackToMerchant={redirectUrl ? backToSite : undefined}
          refreshing={refreshing}
        />
      </div>
    );
  }

  const getBadgeVariant = (): 'danger' | 'success' | 'info' => {
    if (timeRemaining <= 60) return 'danger';
    return 'info';
  };

  const badge = {
    variant: getBadgeVariant(),
    time: formatTime(timeRemaining)
  };

  // Get data from card or details (fallback), support multiple field names
  const phoneNumber = paymentData.card?.pago_movil
    || paymentData.card?.number
    || paymentData.details?.pago_movil
    || paymentData.details?.phone_number
    || '';
  const bankName = paymentData.card?.bank_name
    || paymentData.details?.bank_name
    || '';
  const cedula = paymentData.card?.cedula
    || paymentData.details?.cedula
    || '';
  const recipientName = paymentData.card?.recipient_name
    || paymentData.details?.recipient_name
    || '';

  return (
    <BaseModal
      amount={String(paymentData.amount)}
      currency={paymentData.currency}
      showCloseButton={true}
    >
      <PaymentForm
        initialData={{
          phoneNumber,
          accountHolder: recipientName,
          bankName,
          idNumber: cedula
        }}
        onSubmit={handleFormSubmit}
        confirming={confirming}
        error={error}
        paymentStatus={paymentData.status}
        timeExpired={timeRemaining <= 0}
        badge={badge}
        requiresConfirmRef={requiresConfirmRef}
      />
    </BaseModal>
  );
};

export default PaymentModal;
